import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import ConvertToPrepaidContent, {
  GetOrderInfoData,
  OrderByHashData,
} from '@/components/ConvertToPrepaid';

import { getOrderByHash, getOrderInfoByHash } from '@/services/Orders';

const ConvertToPrepaidDynamicPage: React.FC = () => {
  const router = useRouter();
  const { hash } = router.query;
  const [orderData, setOrderData] = useState<OrderByHashData | null>(null);
  const [orderInfo, setOrderInfo] = useState<GetOrderInfoData | null>(null);
  const [orderInfoExpired, setOrderInfoExpired] = useState(false);
  const [orderInfoMessage, setOrderInfoMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!router.isReady) return;

    const hashStr =
      typeof hash === 'string'
        ? hash
        : Array.isArray(hash)
        ? hash.join('/')
        : '';

    if (!hashStr) {
      setLoading(false);
      setError('Order hash is missing in the URL.');
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    Promise.allSettled([
      getOrderByHash(hashStr),
      getOrderInfoByHash(hashStr),
    ])
      .then(([orderRes, infoRes]) => {
        if (!isMounted) return;

        // Process order-by-hash (UI info like products, address)
        if (orderRes.status === 'fulfilled' && orderRes.value?.data) {
          setOrderData(orderRes.value.data);
        } else if (orderRes.status === 'rejected') {
          const message =
            (orderRes as PromiseRejectedResult).reason?.response?.data
              ?.message || 'Order details could not be found.';
          setError(message);
        }

        // Process get-order-info (Payment & expiration info)
        if (infoRes.status === 'fulfilled' && infoRes.value) {
          const infoVal = infoRes.value;
          if (infoVal.success === false) {
            setOrderInfoExpired(true);
            setOrderInfoMessage(
              infoVal.message ||
                'Time limit to convert order to prepaid has expired',
            );
          } else if (infoVal.data) {
            setOrderInfo(infoVal.data);
            if (
              infoVal.data.expiry_time &&
              new Date(infoVal.data.expiry_time).getTime() <= Date.now()
            ) {
              setOrderInfoExpired(true);
              setOrderInfoMessage(
                'Time limit to convert order to prepaid has expired',
              );
            }
          }
        } else if (infoRes.status === 'rejected') {
          const reason = (infoRes as PromiseRejectedResult).reason;
          const respData = reason?.response?.data;
          if (respData && respData.success === false) {
            setOrderInfoExpired(true);
            setOrderInfoMessage(
              respData.message ||
                'Time limit to convert order to prepaid has expired',
            );
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error('Error fetching order data:', err);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [router.isReady, hash]);

  return (
    <>
      <Head>
        <title>Order Payment | ZebraLearn</title>
      </Head>
      <ConvertToPrepaidContent
        orderData={orderData}
        orderInfo={orderInfo}
        orderInfoExpired={orderInfoExpired}
        orderInfoMessage={orderInfoMessage}
        hash={
          typeof hash === 'string'
            ? hash
            : Array.isArray(hash)
            ? hash.join('/')
            : ''
        }
        loading={loading}
        error={error}
      />
    </>
  );
};

export default ConvertToPrepaidDynamicPage;
